import React, { useCallback, useMemo, useState } from 'react';
import { isNull } from 'lodash/fp';
import { Options } from 'react-select';

import { useAppSelector } from '../../../hooks/react-redux';
import { DropdownOption } from '../dropdown/Dropdown';
import { getBaseEntityDropdownOptions } from '../../admin/entity/store';
import { getAllClientDocumentGroups, getAvailableDocumentNames } from '../../admin/documents/store';
import { UTC_FORMAT, formatDate } from '../../../utils/luxon';
import { FilterDropdownWithLabel } from './FilterDropdownWithLabel';
import { DateFilter } from './DateFilter';
import { Radio } from '../radio/Radio';
import { DateFilter as DateFilterType, PreExecutionFilterOption, SearchFilter } from '../../documents/search/store';
import styles from './Filter.module.scss';
import { Scrollable } from '../scrollable/Scrollable';
import { InformationTooltip } from '../tooltip';
import { Toggle } from '../toggle';

interface DocumentFilterProps {
    to: string | null;
    from: string | null;
    entityId: string[] | null;
    isExclusiveEntityFilter: boolean;
    includePreExecution: string | null;
    documentNameId?: string[] | null;
    documentNameGroupId?: string[] | null;
    showDocumentNameFilter?: boolean;
    updateFilter: (key: keyof SearchFilter, value: string[] | string | null | boolean) => void;
    updateDateFilter: (key: keyof DateFilterType, value: string | null) => void;
    fontSize?: string;
    showRiskScoreFilter?: boolean;
    showOverrideRiskFilter?: boolean;
    showPreferredTermsFilter?: boolean;
    includeRiskScore?: boolean;
    includeRiskOverride?: boolean;
    includePreferredTerms?: boolean;
    disabled?: boolean;
}

export const DocumentFilter: React.FC<DocumentFilterProps> = ({
    to,
    from,
    entityId,
    isExclusiveEntityFilter,
    includePreExecution,
    documentNameId = null,
    documentNameGroupId = null,
    showDocumentNameFilter = true,
    updateFilter,
    updateDateFilter,
    fontSize = '12px',
    showRiskScoreFilter = false,
    showOverrideRiskFilter = false,
    showPreferredTermsFilter = false,
    includeRiskScore = false,
    includeRiskOverride = false,
    includePreferredTerms = false,
    disabled = false
}) => {
    const [exclusiveFilterWarningVisible, setExclusiveFilterWarningVisible] = useState(false);
    const entityOptions = useAppSelector(getBaseEntityDropdownOptions);
    const allDocumentNames = useAppSelector(getAvailableDocumentNames);
    const allDocumentNameGroups = useAppSelector(getAllClientDocumentGroups);

    const updateToDate = (value: Date | null) => updateDateFilter('to', isNull(value) ? value : formatDate(value, UTC_FORMAT));
    const updateFromDate = (value: Date | null) => updateDateFilter('from', isNull(value) ? value : formatDate(value, UTC_FORMAT));
    const toggleExclusiveFilter = () => {
        updateFilter('entityId', entityId?.slice(0, 2) || null);
        updateFilter('isExclusiveEntityFilter', !isExclusiveEntityFilter);
        setExclusiveFilterWarningVisible(false);
    };

    const preExecutionOptions: DropdownOption[] = useMemo(() => Object.values(PreExecutionFilterOption).map(value => ({ label: value, value, disabled })), [disabled]);

    const mappedDocumentNames: DropdownOption[] = allDocumentNames.map(({ documentNameId, documentName }) => ({ value: documentNameId!.toString(), label: documentName, disabled }));
    const mappedDocumentNameGroups: DropdownOption[] = allDocumentNameGroups.map(({ clientDocumentGroupId, groupName }, i) => ({ value: clientDocumentGroupId!.toString(), label: groupName, isGroup: true, showDivider: i === allDocumentNameGroups.length - 1, disabled }));
    const documentGroupNamesAndDocumentNamesOptions = [...mappedDocumentNameGroups, ...mappedDocumentNames];

    const entityFilter = useMemo(() => {
        const getEntityName = (val: string) => entityOptions.find(({ value }) => value === val)!.label;
        if (isNull(entityId)) {
            return null;
        }
        return entityId.map(value => ({ value, label: getEntityName(value), disabled }));
    }, [entityId, entityOptions, disabled]);

    const documentNameAndGroupFilter = useMemo(() => {
        let documentNameFilter: DropdownOption[] | null = null;
        let documentGroupFilter: DropdownOption[] | null = null;
        const getDocumentName = (value: string) => allDocumentNames.find(({ documentNameId }) => documentNameId?.toString() === value)!.documentName;
        const getDocumentGroupName = (value: string) => allDocumentNameGroups.find(({ clientDocumentGroupId }) => clientDocumentGroupId?.toString() === value)!.groupName;

        if (!isNull(documentNameId)) {
            documentNameFilter = documentNameId.map(value => ({ value, label: getDocumentName(value) }));
        }

        if (!isNull(documentNameGroupId)) {
            documentGroupFilter = documentNameGroupId.map(value => ({ value, label: getDocumentGroupName(value), isGroup: true }));
        }

        if (isNull(documentNameFilter) && isNull(documentGroupFilter)) {
            return null;
        }

        return [...(documentNameFilter || []), ...(documentGroupFilter || [])];
    }, [documentNameId, allDocumentNames, allDocumentNameGroups, documentNameGroupId]);

    const preExecutionFilter = useMemo(() => preExecutionOptions.find(({ value }) => value === includePreExecution) || null, [includePreExecution, preExecutionOptions]);

    const updatePreExecutionFilter = (option: DropdownOption | Options<DropdownOption> | null) => {
        let value = null;
        if (!isNull(option)) {
            value = (option as DropdownOption).value;
        }
        updateFilter('includePreExecution', value);
    };

    const updateEntitiesFilter = (option: DropdownOption | Options<DropdownOption> | null) => {
        let value = null;
        if (!isNull(option) && (option as Options<DropdownOption>).length) {
            value = (option as Options<DropdownOption>).map(({ value }) => value);
            if (isExclusiveEntityFilter && value.length > 2) {
                setExclusiveFilterWarningVisible(true);
                return;
            }
            setExclusiveFilterWarningVisible(false);
        }
        updateFilter('entityId', value);
    };

    const updateDocumentFilter = (option: DropdownOption | Options<DropdownOption> | null) => {
        let documentNameIds = null;
        let groupDocumentNameIds = null;
        if (!isNull(option) && (option as Options<DropdownOption>).length) {
            documentNameIds = (option as Options<DropdownOption>).filter(({ isGroup }) => !isGroup).map(({ value }) => value) || null;
            groupDocumentNameIds = (option as Options<DropdownOption>).filter(({ isGroup }) => isGroup).map(({ value }) => value) || null;
        }
        updateFilter('documentNameId', documentNameIds);
        updateFilter('documentNameGroupId', groupDocumentNameIds);
    };

    const toggleIncludeRiskScore = useCallback((checked: boolean) => updateFilter('includeRiskScore', checked), [updateFilter]);
    const toggleIncludeRiskOverride = useCallback((checked: boolean) => updateFilter('includeRiskOverride', checked), [updateFilter]);
    const toggleIncludePreferredTerms = useCallback((checked: boolean) => updateFilter('includePreferredTerms', checked), [updateFilter]);

    const toDate = isNull(to) ? null : new Date(to);
    const fromDate = isNull(from) ? null : new Date(from);

    return (
        <Scrollable height='100' width='100%'>
            <div className={styles.filterSettingsWrapper}>
                <div className={styles.exclusiveWrapper}>
                    <Radio
                        checked={isExclusiveEntityFilter}
                        onChange={toggleExclusiveFilter}
                        onClick={toggleExclusiveFilter}
                        showWarning={exclusiveFilterWarningVisible}
                        warning='Max 2 options for exclusive filter'
                        testId='exclusive-filter'
                        label='Exclusive Filter'
                        fontSize={fontSize}
                        disabled={disabled}
                    />
                </div>
                <FilterDropdownWithLabel
                    label='Parties'
                    value={entityFilter}
                    options={entityOptions}
                    onChange={updateEntitiesFilter}
                    testId='entities'
                    fontSize={fontSize}
                />
                {showDocumentNameFilter &&
                    <FilterDropdownWithLabel
                        label='Documents'
                        value={documentNameAndGroupFilter}
                        options={documentGroupNamesAndDocumentNamesOptions}
                        onChange={updateDocumentFilter}
                        testId='documents'
                        fontSize={fontSize}
                    />
                }
                <FilterDropdownWithLabel
                    label='Include Pre-Execution Documents'
                    value={preExecutionFilter}
                    options={preExecutionOptions}
                    onChange={updatePreExecutionFilter}
                    testId='pre-execution'
                    fontSize={fontSize}
                    isMulti={false}
                    isClearable={false}
                />
                <DateFilter
                    updateFromDate={updateFromDate}
                    updateToDate={updateToDate}
                    fromDate={fromDate}
                    toDate={toDate}
                    label='Executed Date'
                    fontSize={fontSize}
                    disabled={disabled}
                />
                {showPreferredTermsFilter &&
                    <div className={styles.preferredTermsFilterWrapper}>
                        <div className={styles.preferredTermsFilterHeader}>
                            <div className={styles.preferredTermsFilterLabel}>Include Preferred Terms</div>
                            <InformationTooltip content='If selected, the report will be populated with an initial row showing the terms defined as "Highly Desirable" in your Risk Tolerance configuration' />
                        </div>
                        <div className={styles.preferredTermsToggleWrapper}>
                            <Toggle checked={includePreferredTerms} onChange={toggleIncludePreferredTerms} />
                        </div>
                    </div>
                }
                {showRiskScoreFilter &&
                    <div className={styles.riskScoreFilterWrapper}>
                        <div className={styles.riskScoreFilterHeader}>
                            <div className={styles.riskScoreFilterLabel}>Include Risk Score</div>
                            <InformationTooltip content='If selected, any fields selected for your report which have a Risk Tolerance configuration will be colour coded in the generated report' />
                        </div>
                        <div className={styles.riskScoreToggleWrapper}>
                            <Toggle checked={includeRiskScore} onChange={toggleIncludeRiskScore} />
                        </div>
                    </div>
                }
                {showOverrideRiskFilter &&
                    <div className={styles.riskScoreFilterWrapper}>
                        <div className={styles.riskScoreFilterHeader}>
                            <div className={styles.riskScoreFilterLabel}>Include Risk Override</div>
                            <InformationTooltip content='If selected, any fields selected for your report which have a Risk Tolerance configuration will be colour coded in the generated report based on the latest overwritten score including the latest justification message' />
                        </div>
                        <div className={styles.riskScoreToggleWrapper}>
                            <Toggle checked={includeRiskOverride} onChange={toggleIncludeRiskOverride} disabled={!includeRiskScore} />
                        </div>
                    </div>
                }
            </div>
        </Scrollable>
    );
};
